import Vue from "vue";
import store from '@/store/store'
import { createAuth0Client } from "@auth0/auth0-spa-js";
// import {ROLE_ADMINISTRATOR, ROLE_INDIVIDUAL, ROLE_PRODUCER, ROLE_UPLOADER} from '@/acl/roles';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getAuthInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
   onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
   redirectUri = window.location.origin,
   ...options
 }) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        auth0ClientSetup: null,
        popupOpen: false,
        error: null
      };
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
      },
      /** Authenticates the user using the redirect method */
      /** check: @auth0/auth0-spa-js/src/Auth0Client.ts */
      async loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },

      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      async getTokenSilently(o) {
        return await this.auth0Client.getTokenSilently(o).then((t) => {
          return t
        }).catch(async (e) => {
          if (e.error === 'login_required') {
            return await store.dispatch('auth/logout');
          }
        });
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      async logout(logoutRedirect = process.env.VUE_APP_LANDING_PAGE_URL, o) {
        try {
          return this.auth0Client.logout({
            logoutParams: {
              returnTo: logoutRedirect,
            },
            ...o
          });
        } catch (e) {
          console.log(e);
        }
      },
      async checkAuth() {
        if (!this.isAuthenticated) {
          return false;
        }

        try {
          await this.fetchAndSetUser();
          return true;
        } catch (e) {
          return false;
        }
      },
      async fetchAndSetUser() {
        this.user = await this.auth0Client.getUser();

        const audience = 'https://' + process.env.VUE_APP_AUTH0_AUDIENCE;
        const metadata = this.user[audience];
        const company = metadata.roles;

        // store user details
        await store.dispatch("auth/setUser", {
          'sub': this.user.sub,
          'name': this.user.name,
          'email': this.user.email,
          'picture': this.user.picture,
          'company': company,
        });

        // get access token
        await store.dispatch("auth/fetchAccessToken");
      },
      async handleCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        await store.dispatch('auth/setAccessDenied', true);

        // handle errors
        if (urlParams.get('error')) {
          // password has expired
          if (urlParams.get('error_description') == 'please change your password') {
            await store.dispatch('auth/setPasswordExpired', true);
          }

          // access denied
          this.loading = false;
          return;
        }

        // get user, check role and configure access token
        if (urlParams.get('code') && urlParams.get('state')) {
          // handle the redirect and retrieve tokens
          try {
            const { appState } = await this.auth0Client.handleRedirectCallback();
            onRedirectCallback(appState);
            
            // store user details
            this.user = await this.auth0Client.getUser();

            // check user has valid role - but not on dev
            const audience = 'https://' + process.env.VUE_APP_AUTH0_AUDIENCE;
            const metadata = this.user[audience];
            const company = metadata.roles;

            // check user has valid role
            let roles = metadata.roles.roles;

            if (roles.length === 0) {
              this.loading = false;
              return;
            }

            const validRole = roles.filter((role) =>
                role.applications[0].ExternalIdentity_Key === process.env.VUE_APP_AUTH0_CLIENT_ID
            ).length > 0;

            if (!validRole) {
              this.loading = false;
              return;
            }

            // store user details
            await store.dispatch('auth/setUser', {
              'sub': this.user.sub,
              'name': this.user.name,
              'email': this.user.email,
              'picture': this.user.picture,
              'company': company,
            });

            // get access token
            await store.dispatch('auth/fetchAccessToken');

            // if we are here we must be ok
            await store.dispatch('auth/setAccessDenied', false);

            // set loading to false which will trigger redirect to dashboard
            // in Callback.vue
            this.isAuthenticated = true;

          } catch (e) {
            // likely invalid state so logout
            console.log(e);
            await store.dispatch('auth/logout');
          }
        }
      },
      async setup() {
        this.auth0Client = await this.auth0ClientSetup;

        const onCallbackPage = (window.location.pathname === '/callback');

        try {
          if (onCallbackPage) {
            await this.handleCallback();
            return;
          }

          // if we are not authenticated sent to auth0
          if (!store.getters['auth/user']) {
            let sendToLandingPage = false;

            if (!document.referrer) {
              sendToLandingPage = true;
            } else {
              const url = new URL(document.referrer);

              sendToLandingPage = !(url.origin === process.env.VUE_APP_LANDING_PAGE_URL || url.origin === process.env.VUE_APP_IMPERSONATE_LOGOUT_REDIRECT);
            }

            if (sendToLandingPage) {
              window.location.assign(process.env.VUE_APP_LANDING_PAGE_URL);
              return;
            }
            
            await this.loginWithRedirect();
          }

          // we have a valid user
          await store.dispatch('auth/setAccessDenied', false);
          this.isAuthenticated = true;

        } catch (e) {
          console.log(e);
          this.error = e;
        }
      },
      // This method check whether we have a valid token. If we dont, we get logged out.
      // The token timer is set Tenant wide under the Settings -> Advanced page (Inactivity timeout)
      isTokenValid() {
        return Boolean(this.getTokenSilently());
      }
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0ClientSetup = createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        authorizationParams: {
          audience: options.audience,
          redirect_uri: redirectUri,
        },
        useRefreshTokens: true,
        cacheLocation: "localstorage",
      });
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};
